import clsx from 'clsx';
import HubertInput from 'components/HubertUI/HubertInput';
import { CircleMinus, CirclePlus } from 'lucide-react';
import type { ComponentProps } from 'react';
import { useEffect, useState } from 'react';
import type { TMatrixRow } from '@hubertai/screening-question-types';
import { hasLanguageKeyAndStringValue } from '@hubertai/screening-question-types';
import { ECreateQuestionMode, useQuestionEditorContext } from '../../../context/create-question.context';
import { useAppDispatch, useAppSelector } from 'rtk/query/hooks';
import { selectQuestionMode, selectQuestionLanguage } from 'rtk/questionEditorSelector';
import { updateMatrixRowColumnByIndex, removeMatrixRowByIndex, addMatrixRowByIndex } from 'rtk/Question.Editor.Slice';
import { useTranslation } from 'react-i18next';

interface Props extends ComponentProps<'div'> {
  row: TMatrixRow;
  rowIndex: number;
  formIndex: number;
}

const FormMatrixRow = ({ row, formIndex, rowIndex, ...props }: Props) => {
  const { t } = useTranslation('Questionsv2');
  const [label, setLabel] = useState<string>('');

  const dispatch = useAppDispatch();
  const { questionId } = useQuestionEditorContext();
  const mode = useAppSelector((state) => selectQuestionMode(state, questionId));
  const questionLanguage = useAppSelector((state) => selectQuestionLanguage(state, questionId));

  useEffect(() => {
    if (row && row.label && hasLanguageKeyAndStringValue(row.label, questionLanguage)) {
      setLabel(row.label[questionLanguage]);
    } else {
      setLabel('');
    }
  }, [questionLanguage, row]);

  return (
    <div {...props} className={clsx('flex flex-row justify-between', 'hubert-vertical-center', 'w-full', 'my-2')}>
      <div className={clsx('w-full', mode !== ECreateQuestionMode.InTemplateEdit && 'w-[80%]')}>
        <HubertInput
          value={label}
          type="text"
          placeholder={t('form.matrix.rowTextPlaceholder')}
          disabled={mode === ECreateQuestionMode.InTemplateEdit}
          onChange={(e) => {
            setLabel(e.target.value);
          }}
          onBlur={(e) => {
            dispatch(
              updateMatrixRowColumnByIndex({
                questionId,
                formIndex,
                fieldName: 'rows',
                fieldIndex: rowIndex,
                updateFieldName: 'label',
                updateFieldValue: e.target.value,
                questionLanguage,
              }),
            );
          }}
        />
      </div>

      {mode !== ECreateQuestionMode.InTemplateEdit ? (
        <div className="flex flex-col justify-between">
          <CircleMinus
            className="cursor-pointer"
            onClick={() => {
              dispatch(removeMatrixRowByIndex({ questionId, formIndex, rowIndex }));
            }}
          />

          <CirclePlus
            className="cursor-pointer"
            onClick={() => {
              dispatch(addMatrixRowByIndex({ questionId, formIndex, rowIndex: rowIndex + 1, questionLanguage }));
            }}
          />
        </div>
      ) : null}
    </div>
  );
};

export default FormMatrixRow;
